/* ─────────────────────────────────────────────────────────────────
   helpers.js  —  KFX Portal  (advanced calculation engine)

   FIXES & IMPROVEMENTS:
   1. calcConsecWinStreak — DAY-based streak: a day is a win if its
      net P&L > 0; counts consecutive winning DAYS from most recent.
      A losing or breakeven day breaks the streak.
   2. calcStats.maxConsec — tracks both max loss streak AND max win
      streak all-time (both day-based); breakeven day resets both.
   3. calcStats.avgrr — only averages trades that actually have an RR
      value (> 0), skipping trades with no RR recorded.
   4. calcStats.pf — guards against grossLoss = 0.
   5. calcStats.net / aw / al — rounded to 2dp, no float bleed.
   6. calcStats.bestPair — best pair by win-rate (min 3 trades),
      falls back to best PnL pair if no pair has ≥ 3 trades.
   7. calcStats.expectancy — per-trade average P&L.
   8. calcStats — adds sharpeRatio, recoveryFactor, maxWinStreak,
      avgWin, avgLoss, largestWin, largestLoss, bestDay, worstDay,
      longWR, shortWR for advanced dashboard panels.
   9. fmt$ — guards negative zero.
───────────────────────────────────────────────────────────────── */

// ── Internal sort: date ASC, then id ASC as stable tiebreaker ──
function sortedByDate(trades) {
  return [...trades].sort((a, b) => {
    const da = (a.date || '').localeCompare(b.date || '');
    if (da !== 0) return da;
    return (Number(a.id) || 0) - (Number(b.id) || 0);
  });
}

// ── Safe numeric coercion from any field format ─────────────────
function num(v) {
  const n = parseFloat(v);
  return isFinite(n) ? n : 0;
}

// ── Round to 2 decimal places, avoiding float bleed ────────────
function r2(n) {
  return Math.round(n * 100) / 100;
}

/* ═══════════════════════════════════════════════════════════════
   calcStats(trades) → comprehensive stats object
   Single source of truth for ALL pages.
═══════════════════════════════════════════════════════════════ */
export function calcStats(trades) {
  const empty = {
    total: 0, wins: 0, losses: 0, breakevens: 0, wr: 0,
    net: 0, aw: 0, al: 0,
    avgrr: '0.00', pf: '0.00',
    bestPair: '—', expectancy: '0.00',
    maxConsec: 0, maxWinStreak: 0,
    sharpeRatio: '0.00', recoveryFactor: '0.00',
    largestWin: 0, largestLoss: 0,
    longWR: 0, shortWR: 0,
    bestDay: '—', worstDay: '—',
  };
  if (!trades || !trades.length) return empty;

  const sorted = sortedByDate(trades);

  let wins = 0, losses = 0, breakevens = 0;
  let grossWin = 0, grossLoss = 0;
  let sumRR = 0, rrCount = 0;
  let sumWin = 0, sumLoss = 0;
  let largestWin = 0, largestLoss = 0;

  // Direction breakdown
  let longWins = 0, longTotal = 0, shortWins = 0, shortTotal = 0;

  // Pair stats for bestPair
  const pairStats = {};

  // Day P&L for bestDay / worstDay
  const dayPnl = {};

  // ── Day-based max streaks (skipping no-trade days) ───────────
  // Build daily net P&L map, then walk day by day
  const dayPnlForStreak = {};
  sorted.forEach(t => {
    const date = t.date || '';
    if (date) dayPnlForStreak[date] = (dayPnlForStreak[date] || 0) + num(t.result);
  });
  const tradedDays = Object.keys(dayPnlForStreak).sort();
  let maxConsec = 0, curLossStreak = 0;
  let maxWinStreak = 0, curWinStreak = 0;
  let bestStreakDays = [], curStreakDays = [];
  tradedDays.forEach(day => {
    const pnl = dayPnlForStreak[day];
    if (pnl > 0) {
      curLossStreak = 0;
      curWinStreak++;
      curStreakDays.push(day);
      if (curWinStreak > maxWinStreak) {
        maxWinStreak = curWinStreak;
        bestStreakDays = [...curStreakDays];
      }
    } else if (pnl < 0) {
      curWinStreak = 0;
      curStreakDays = [];
      curLossStreak++;
      if (curLossStreak > maxConsec) maxConsec = curLossStreak;
    } else {
      // breakeven day resets both
      curWinStreak = 0;
      curStreakDays = [];
      curLossStreak = 0;
    }
  });
  // Log the best streak days for debugging
  if (typeof window !== 'undefined') {
    console.log('[KFX] Best win streak:', maxWinStreak, 'days');
    console.log('[KFX] Streak days:', bestStreakDays.join(', '));
  }

  // Per-trade results array for Sharpe
  const results = [];

  sorted.forEach(t => {
    const result = num(t.result);
    // Only count RR if it has a meaningful value.
    // ?? only catches null/undefined — use parseFloat to handle empty strings too.
    const _actualRR = parseFloat(t.actualRR);
    const _rr       = parseFloat(t.rr);
    const rrVal     = (isFinite(_actualRR) && _actualRR > 0) ? _actualRR
                    : (isFinite(_rr)       && _rr       > 0) ? _rr
                    : 0;
    const pair   = t.pair || 'Unknown';
    const dir    = (t.dir || '').toUpperCase();
    const date   = t.date || '';

    results.push(result);

    // Day P&L
    if (date) dayPnl[date] = (dayPnl[date] || 0) + result;

    // Pair map
    if (!pairStats[pair]) pairStats[pair] = { pnl: 0, wins: 0, n: 0 };
    pairStats[pair].pnl += result;
    pairStats[pair].n++;

    // Direction
    if (dir === 'BUY' || dir === 'LONG') {
      longTotal++;
      if (result > 0) longWins++;
    } else if (dir === 'SELL' || dir === 'SHORT') {
      shortTotal++;
      if (result > 0) shortWins++;
    }

    if (result > 0) {
      wins++;
      grossWin  += result;
      sumWin    += result;
      if (result > largestWin) largestWin = result;
      pairStats[pair].wins++;
    } else if (result < 0) {
      losses++;
      grossLoss += Math.abs(result);
      sumLoss   += result;
      if (Math.abs(result) > largestLoss) largestLoss = Math.abs(result);
    } else {
      // Breakeven
      breakevens++;
    }

    // Only count RR for trades that actually have it
    if (rrVal > 0) {
      sumRR += rrVal;
      rrCount++;
    }
  });

  const total = trades.length;
  const net   = r2(grossWin - grossLoss);
  const wr    = total   ? Math.round(wins / total * 100)   : 0;
  const aw    = wins    ? r2(sumWin / wins)                : 0;
  const al    = losses  ? r2(sumLoss / losses)             : 0;

  // avgrr: only over trades with a recorded RR
  const avgrr = rrCount > 0 ? (sumRR / rrCount).toFixed(2) : '0.00';

  // Profit factor
  const pf = grossLoss > 0
    ? (grossWin / grossLoss).toFixed(2)
    : grossWin > 0 ? '∞' : '0.00';

  // Expectancy: average P&L per trade
  const expectancy = total ? (net / total).toFixed(2) : '0.00';

  // Best pair: highest win-rate among pairs with ≥ 3 trades
  const qualifiedPairs = Object.entries(pairStats).filter(([, d]) => d.n >= 3);
  const bestPair = qualifiedPairs.length
    ? qualifiedPairs.sort((a, b) => (b[1].wins / b[1].n) - (a[1].wins / a[1].n))[0][0]
    : Object.keys(pairStats).length
      ? Object.entries(pairStats).sort((a, b) => b[1].pnl - a[1].pnl)[0][0]
      : '—';

  // Direction win rates
  const longWR  = longTotal  ? Math.round(longWins  / longTotal  * 100) : 0;
  const shortWR = shortTotal ? Math.round(shortWins / shortTotal * 100) : 0;

  // Best / worst day
  const dayEntries = Object.entries(dayPnl);
  const bestDay  = dayEntries.length ? dayEntries.sort((a, b) => b[1] - a[1])[0][0] : '—';
  const worstDay = dayEntries.length ? dayEntries.sort((a, b) => a[1] - b[1])[0][0] : '—';

  // Sharpe ratio (simplified daily): mean(results) / stddev(results)
  let sharpeRatio = '0.00';
  if (results.length >= 2) {
    const mean   = results.reduce((s, v) => s + v, 0) / results.length;
    const stddev = Math.sqrt(results.reduce((s, v) => s + Math.pow(v - mean, 2), 0) / results.length);
    sharpeRatio  = stddev > 0 ? (mean / stddev).toFixed(2) : mean > 0 ? '∞' : '0.00';
  }

  // Recovery factor: net P&L / max drawdown
  const { maxDD } = calcMaxDrawdown(trades);
  const recoveryFactor = maxDD > 0 ? (net / maxDD).toFixed(2) : net > 0 ? '∞' : '0.00';

  return {
    total, wins, losses, breakevens, wr,
    net, aw, al,
    avgrr, pf,
    bestPair, expectancy,
    maxConsec, maxWinStreak,
    sharpeRatio, recoveryFactor,
    largestWin: r2(largestWin),
    largestLoss: r2(largestLoss),
    longWR, shortWR,
    bestDay, worstDay,
  };
}

/* ═══════════════════════════════════════════════════════════════
   calcConsecWinStreak(trades) → number
   WEEK-based clean-week count: counts ALL weeks across all time
   that had at least 1 traded day and zero red days (no single day
   with net P&L < 0). Weeks with no trades are ignored entirely.
   This is a total count — not a consecutive streak.
=============================================================== */
export function calcConsecWinStreak(trades) {
  if (!trades || !trades.length) return 0;

  // Step 1: build daily net P&L map
  const dayPnl = {};
  trades.forEach(t => {
    const date = t.date || '';
    if (!date) return;
    dayPnl[date] = (dayPnl[date] || 0) + num(t.result);
  });

  // Step 2: helper - get week key = the Monday date "YYYY-MM-DD" of that week
  // Using Monday's date directly avoids all ISO week-number edge cases
  // (year boundaries, month splits, etc.)
  const getWeekKey = dateStr => {
    const [y, m, d] = dateStr.split('-').map(Number);
    const date = new Date(y, m - 1, d);
    const day = date.getDay(); // 0=Sun,1=Mon,...,6=Sat
    const diff = (day === 0 ? -6 : 1 - day); // shift back to Monday
    const mon = new Date(date);
    mon.setDate(date.getDate() + diff);
    // Return "YYYY-MM-DD" of that Monday — sorts correctly as a string
    const my = mon.getFullYear();
    const mm = String(mon.getMonth() + 1).padStart(2, '0');
    const md = String(mon.getDate()).padStart(2, '0');
    return my + '-' + mm + '-' + md;
  };

  // Step 3: group days into weeks
  const weekMap = {};
  Object.entries(dayPnl).forEach(([date, pnl]) => {
    const wk = getWeekKey(date);
    if (!weekMap[wk]) weekMap[wk] = { hasRed: false, hasTrades: false };
    weekMap[wk].hasTrades = true;
    if (pnl < 0) weekMap[wk].hasRed = true;
  });

  // Step 4: count ALL traded weeks that had zero red days
  let cleanWeeks = 0;
  Object.values(weekMap).forEach(wk => {
    if (wk.hasTrades && !wk.hasRed) cleanWeeks++;
  });
  return cleanWeeks;
}

/* ═══════════════════════════════════════════════════════════════
   calcMaxDrawdown(trades) → { maxDD, maxDDPct }
   Equity-curve drawdown — same logic as DashboardPage had,
   but now uses the stable sort so DD is deterministic.
═══════════════════════════════════════════════════════════════ */
export function calcMaxDrawdown(trades) {
  if (!trades || !trades.length) return { maxDD: 0, maxDDPct: 0 };
  const sorted = sortedByDate(trades);
  let peak = 0, equity = 0, maxDD = 0, maxDDPct = 0;
  sorted.forEach(t => {
    equity += num(t.result);
    if (equity > peak) peak = equity;
    const dd    = peak - equity;
    const ddPct = peak > 0 ? (dd / peak) * 100 : 0;
    if (dd > maxDD) { maxDD = dd; maxDDPct = ddPct; }
  });
  return { maxDD: r2(maxDD), maxDDPct: r2(maxDDPct) };
}

/* ═══════════════════════════════════════════════════════════════
   calcScore(stats, maxDDPct, tradeCount) → number | null
   Requires ≥ 10 trades. Mirrors DashboardPage formula exactly,
   but now fed correct stats (especially avgrr = actualRR avg).
═══════════════════════════════════════════════════════════════ */
const MIN_TRADES_FOR_SCORE = 10;

export function calcScore(s, maxDDPct, tradeCount) {
  if (tradeCount < MIN_TRADES_FOR_SCORE) return null;
  const wrScore   = s.wr;
  const rrScore   = Math.min(100, parseFloat(s.avgrr) / 3 * 100);
  const pfScore   = s.pf === '∞' ? 100 : Math.min(100, parseFloat(s.pf) / 3 * 100);
  const ddPenalty = Math.min(40, maxDDPct * 0.8);
  const confBonus = Math.min(10, (tradeCount - MIN_TRADES_FOR_SCORE) / 20);
  const raw = wrScore * 0.30 + rrScore * 0.28 + pfScore * 0.22 - ddPenalty + confBonus;
  return Math.min(100, Math.max(0, Math.round(raw)));
}

/* ═══════════════════════════════════════════════════════════════
   scoreLabel(score) → { text, color }
   Same buckets as original — kept identical so UI is unchanged.
═══════════════════════════════════════════════════════════════ */
export function scoreLabel(score) {
  if (score >= 85) return { text: 'Elite',      color: '#C9A84C' };
  if (score >= 70) return { text: 'Strong',     color: '#3DBE73' };
  if (score >= 55) return { text: 'Good',       color: '#4E7FD4' };
  if (score >= 40) return { text: 'Fair',       color: '#E2A542' };
  if (score >= 25) return { text: 'Developing', color: '#E2A542' };
  return               { text: 'Needs Work',  color: '#D94F4F' };
}

/* ═══════════════════════════════════════════════════════════════
   fmt$(value, sign?) → "$1,234.56" | "+$1,234.56" | "-$1,234.56"
   sign defaults to true.
═══════════════════════════════════════════════════════════════ */
export function fmt$(value, sign = true) {
  const n = num(value);
  // Guard negative zero
  if (n === 0) return sign ? '$0' : '$0';
  const abs = Math.abs(n).toLocaleString('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
  if (!sign) return (n < 0 ? '-' : '') + '$' + abs;
  return (n >= 0 ? '+$' : '-$') + abs;
}

/* ═══════════════════════════════════════════════════════════════
   sanitizeHTML(html) → string
   XSS choke-point for NotebookPage. Strips all tags except a
   safe whitelist, and removes all event-handler attributes.
   Uses DOMParser so no innerHTML-on-document risk.
═══════════════════════════════════════════════════════════════ */
export function sanitizeHTML(html) {
  if (!html) return '';
  const ALLOWED_TAGS = new Set([
    'b','strong','i','em','u','s','strike','br','p','div','span',
    'h1','h2','h3','h4','ul','ol','li','blockquote','hr','img',
  ]);
  const ALLOWED_ATTRS = new Set(['src','alt','width','height','class','style','data-nb-img','data-nb-id']);

  const doc  = new DOMParser().parseFromString(html, 'text/html');
  const walk = node => {
    const children = [...node.childNodes];
    for (const child of children) {
      if (child.nodeType === Node.ELEMENT_NODE) {
        const tag = child.tagName.toLowerCase();
        if (!ALLOWED_TAGS.has(tag)) {
          // Replace disallowed element with its children (keep text)
          while (child.firstChild) node.insertBefore(child.firstChild, child);
          node.removeChild(child);
        } else {
          // Strip disallowed attributes
          [...child.attributes].forEach(attr => {
            if (!ALLOWED_ATTRS.has(attr.name.toLowerCase())) {
              child.removeAttribute(attr.name);
            }
          });
          // Block javascript: in src/href
          ['src', 'href'].forEach(a => {
            const v = child.getAttribute(a);
            if (v && /^\s*javascript:/i.test(v)) child.removeAttribute(a);
          });
          walk(child);
        }
      } else if (child.nodeType === Node.COMMENT_NODE) {
        node.removeChild(child);
      }
    }
  };
  walk(doc.body);
  return doc.body.innerHTML;
}

/* ═══════════════════════════════════════════════════════════════
   serializeNoteHTML(html) → string
   Before saving to DB, strips the injected label bar (buttons,
   caption, controls) from every .nb-img-block so only the bare
   <div class="nb-img-block" data-nb-img="1" data-nb-id="...">
     <img .../>
   </div>
   is persisted. The label bar is rebuilt by hydrateImages() on load.
═══════════════════════════════════════════════════════════════ */
export function serializeNoteHTML(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  doc.body.querySelectorAll('.nb-img-block').forEach(block => {
    // Remove everything except the <img>
    [...block.childNodes].forEach(child => {
      if (child.nodeName !== 'IMG') child.remove();
    });
    block.setAttribute('data-nb-img', '1');
    block.removeAttribute('contenteditable');
  });
  return sanitizeHTML(doc.body.innerHTML);
}

/* ═══════════════════════════════════════════════════════════════
   exportPDF — two call signatures:
     exportPDF(title, htmlBody)  → NotebookPage: print a note
     exportPDF(trades[])         → other pages:  print trade table
   Returns true on success, false if window.open was blocked.
═══════════════════════════════════════════════════════════════ */
export function exportPDF(titleOrTrades, htmlBody) {
  // ── Notebook note mode: exportPDF(title, htmlBody) ──────────
  if (typeof titleOrTrades === 'string') {
    const title = titleOrTrades || 'KFX Note';

    // ── Strip dark-theme inline styles from the note body ──────
    // The editor stores rich HTML with rgba(...) dark colours as
    // inline styles. We parse the body, remove every inline style
    // attribute (except on <img>), and let the print CSS take over.
    const cleanBody = (() => {
      if (!htmlBody) return '';
      const doc = new DOMParser().parseFromString(htmlBody, 'text/html');

      // Remove injected UI chrome: image label bars, expand/remove buttons
      doc.body.querySelectorAll('.nb-img-label, .nb-img-controls, .nb-img-ctrl-btn, button').forEach(el => el.remove());

      // Strip inline styles from everything except <img> (keep src sizing)
      doc.body.querySelectorAll('*').forEach(el => {
        if (el.tagName !== 'IMG') el.removeAttribute('style');
      });

      // Re-class trade recap header for print styling
      doc.body.querySelectorAll('[class]').forEach(el => el.removeAttribute('class'));

      // Unwrap contenteditable wrappers that held image blocks
      doc.body.querySelectorAll('[contenteditable]').forEach(el => {
        el.removeAttribute('contenteditable');
      });

      return doc.body.innerHTML;
    })();

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>${title}</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    body {
      font-family: 'Segoe UI', Arial, sans-serif;
      font-size: 13px;
      color: #1a1a1a;
      background: #fff;
      padding: 40px 52px;
      max-width: 820px;
      margin: 0 auto;
      line-height: 1.7;
    }

    /* ── Header ── */
    .kfx-header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      padding-bottom: 14px;
      border-bottom: 2px solid #C9A84C;
      margin-bottom: 24px;
    }
    .kfx-header-title {
      font-size: 22px;
      font-weight: 700;
      color: #111;
      letter-spacing: -.01em;
    }
    .kfx-header-meta {
      font-size: 10px;
      color: #888;
      margin-top: 4px;
      letter-spacing: .04em;
    }
    .kfx-logo {
      font-size: 11px;
      font-weight: 800;
      letter-spacing: .18em;
      color: #C9A84C;
      text-align: right;
      margin-top: 3px;
    }

    /* ── Body text ── */
    p { margin-bottom: 6px; }
    h2 {
      font-size: 11px;
      font-weight: 700;
      letter-spacing: .14em;
      text-transform: uppercase;
      color: #C9A84C;
      margin: 22px 0 8px;
      padding-bottom: 5px;
      border-bottom: 1px solid #e8e0cc;
    }
    ul, ol { padding-left: 20px; margin: 6px 0 10px; }
    li { margin-bottom: 3px; }
    b, strong { font-weight: 700; }
    i, em { font-style: italic; }
    u { text-decoration: underline; }
    s, strike { text-decoration: line-through; }

    /* ── Images ── */
    img {
      max-width: 100%;
      height: auto;
      display: block;
      margin: 12px 0;
      border: 1px solid #e0e0e0;
      border-radius: 6px;
    }

    /* ── Trade recap summary box ── */
    div > div:first-child {
      /* catches the recap header pill — reset any dark bg */
      background: transparent !important;
    }

    /* ── Trade cards — re-style for print ── */
    /* Any div that wraps a trade gets a light border */
    .nb-body > div > div {
      border: 1px solid #e0ddd5 !important;
      border-top: 2px solid #C9A84C !important;
      background: #fafaf8 !important;
      border-radius: 8px !important;
      padding: 14px 16px !important;
      margin-bottom: 12px !important;
      page-break-inside: avoid;
    }

    /* ── Recap summary banner ── */
    .recap-banner {
      background: #fffbf0 !important;
      border: 1px solid #e8d99a !important;
      border-radius: 8px !important;
      padding: 10px 14px !important;
      margin-bottom: 14px !important;
      font-size: 11px;
      color: #7a6428;
    }

    /* ── Reflection grid ── */
    .reflection-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 10px;
      margin: 10px 0 16px;
    }
    .reflection-card {
      border: 1px solid #e0ddd5;
      border-radius: 8px;
      padding: 12px 14px;
      min-height: 80px;
      page-break-inside: avoid;
    }
    .reflection-label {
      font-size: 8px;
      font-weight: 700;
      letter-spacing: .14em;
      text-transform: uppercase;
      color: #999;
      margin-bottom: 6px;
    }

    /* ── Meta pills — flatten for print ── */
    span {
      color: inherit !important;
      background: transparent !important;
      border: none !important;
    }

    /* ── Footer ── */
    .kfx-footer {
      margin-top: 32px;
      padding-top: 10px;
      border-top: 1px solid #e8e0cc;
      font-size: 9px;
      color: #bbb;
      display: flex;
      justify-content: space-between;
    }

    @media print {
      body { padding: 20px 28px; }
      @page { margin: 16mm 14mm; }
    }
  </style>
</head>
<body>
  <div class="kfx-header">
    <div>
      <div class="kfx-header-title">${title}</div>
      <div class="kfx-header-meta">KFX Portal · ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}</div>
    </div>
    <div class="kfx-logo">KFX</div>
  </div>
  <div class="nb-body">${cleanBody}</div>
  <div class="kfx-footer">
    <span>KFX Portal — Trading Notebook</span>
    <span>Exported ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}</span>
  </div>
</body>
</html>`;
    const win = window.open('', '_blank');
    if (!win) return false;
    win.document.write(html);
    win.document.close();
    win.focus();
    setTimeout(() => win.print(), 400); // slight delay so images load first
    return true;
  }

  // ── Trades table mode: exportPDF(trades[]) ──────────────────
  const trades = titleOrTrades;
  if (!trades || !trades.length) return false;
  const sorted = sortedByDate(trades);
  const stats  = calcStats(trades);

  const rows = sorted.map(t => `
    <tr>
      <td>${t.date || ''}</td>
      <td>${t.pair || ''}</td>
      <td>${t.dir || ''}</td>
      <td>${t.strat || ''}</td>
      <td style="text-align:right;color:${num(t.result) >= 0 ? '#3DBE73' : '#D94F4F'}">
        ${fmt$(t.result)}
      </td>
      <td style="text-align:right">${num(t.actualRR ?? t.rr ?? 0).toFixed(2)}</td>
    </tr>`).join('');

  const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8"/>
  <title>KFX Trade Journal Export</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 12px; color: #111; padding: 24px; }
    h1 { font-size: 18px; margin-bottom: 4px; }
    .sub { color: #555; margin-bottom: 16px; font-size: 11px; }
    .stats { display: flex; gap: 24px; margin-bottom: 20px; flex-wrap: wrap; }
    .stat { background: #f4f4f4; border-radius: 6px; padding: 8px 14px; }
    .stat-label { font-size: 10px; color: #777; text-transform: uppercase; }
    .stat-value { font-size: 15px; font-weight: bold; }
    table { width: 100%; border-collapse: collapse; }
    th { background: #222; color: #fff; padding: 6px 8px; text-align: left; font-size: 11px; }
    td { padding: 5px 8px; border-bottom: 1px solid #e5e5e5; }
    tr:nth-child(even) td { background: #fafafa; }
    @media print { body { padding: 0; } }
  </style>
</head>
<body>
  <h1>KFX Trade Journal</h1>
  <div class="sub">Exported ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })} · ${trades.length} trades</div>
  <div class="stats">
    <div class="stat"><div class="stat-label">Net P&amp;L</div><div class="stat-value">${fmt$(stats.net)}</div></div>
    <div class="stat"><div class="stat-label">Win Rate</div><div class="stat-value">${stats.wr}%</div></div>
    <div class="stat"><div class="stat-label">Profit Factor</div><div class="stat-value">${stats.pf}</div></div>
    <div class="stat"><div class="stat-label">Avg RR</div><div class="stat-value">${stats.avgrr}</div></div>
    <div class="stat"><div class="stat-label">Best Pair</div><div class="stat-value">${stats.bestPair}</div></div>
    <div class="stat"><div class="stat-label">Expectancy</div><div class="stat-value">${fmt$(stats.expectancy)}</div></div>
  </div>
  <table>
    <thead>
      <tr>
        <th>Date</th><th>Pair</th><th>Dir</th><th>Strategy</th>
        <th style="text-align:right">Result</th>
        <th style="text-align:right">Actual RR</th>
      </tr>
    </thead>
    <tbody>${rows}</tbody>
  </table>
</body>
</html>`;

  const win = window.open('', '_blank');
  if (!win) return false;
  win.document.write(html);
  win.document.close();
  win.focus();
  win.print();
  return true;
}

/* ═══════════════════════════════════════════════════════════════
   exportTXT — two call signatures:
     exportTXT(title, htmlBody)  → NotebookPage: download note as .txt
     exportTXT(trades[])         → other pages:  download trade log
═══════════════════════════════════════════════════════════════ */
export function exportTXT(titleOrTrades, htmlBody) {
  // ── Notebook note mode: exportTXT(title, htmlBody) ──────────
  if (typeof titleOrTrades === 'string') {
    const title = titleOrTrades || 'KFX Note';
    // Strip HTML tags to get plain text
    const tmp = document.createElement('div');
    tmp.innerHTML = htmlBody || '';
    const plain = (tmp.innerText || tmp.textContent || '').trim();
    const content = [
      '═══════════════════════════════════════',
      `  ${title}`,
      `  KFX Portal · ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}`,
      '═══════════════════════════════════════',
      '',
      plain,
      '',
    ].join('\n');
    const blob = new Blob([content], { type: 'text/plain' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `${title.replace(/[^a-z0-9]/gi, '_').toLowerCase()}_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    return;
  }

  // ── Trades log mode: exportTXT(trades[]) ────────────────────
  const trades = titleOrTrades;
  if (!trades || !trades.length) return;
  const sorted = sortedByDate(trades);
  const s      = calcStats(trades);
  const pad    = (label, value, width = 22) =>
    label.padEnd(width) + String(value);

  const header = [
    '═══════════════════════════════════════',
    '  KFX TRADE JOURNAL EXPORT',
    `  ${new Date().toLocaleDateString('en-US', { dateStyle: 'long' })}  ·  ${trades.length} trades`,
    '═══════════════════════════════════════',
    '',
    '── SUMMARY ─────────────────────────────',
    pad('Net P&L:',         fmt$(s.net)),
    pad('Win Rate:',        s.wr + '%'),
    pad('Wins / Losses:',   `${s.wins} / ${s.losses}`),
    pad('Avg Win:',         fmt$(s.aw)),
    pad('Avg Loss:',        fmt$(s.al)),
    pad('Avg RR:',          s.avgrr),
    pad('Profit Factor:',   s.pf),
    pad('Expectancy:',      fmt$(s.expectancy)),
    pad('Best Pair:',       s.bestPair),
    pad('Max Consec Loss:', s.maxConsec),
    '',
    '── TRADES ──────────────────────────────',
    '',
  ].join('\n');

  const tradeBlocks = sorted.map((t, i) => [
    `[${String(i + 1).padStart(3, '0')}] ${t.date || '—'}  ${t.pair || '—'}  ${t.dir || '—'}`,
    pad('  Strategy:',   t.strat  || '—'),
    pad('  Session:',    t.session || '—'),
    pad('  Result:',     fmt$(t.result)),
    pad('  Actual RR:',  num(t.actualRR ?? t.rr ?? 0).toFixed(2)),
    pad('  Entry/Exit:', `${t.actualEntry || t.entry || '—'} → ${t.actualExit || t.tp || '—'}`),
    t.notes  ? pad('  Notes:',  t.notes)  : null,
    t.review ? pad('  Review:', t.review) : null,
    '  ─────────────────────────────────────',
  ].filter(Boolean).join('\n')).join('\n');

  const blob = new Blob([header + tradeBlocks + '\n'], { type: 'text/plain' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `kfx_trades_${new Date().toISOString().slice(0, 10)}.txt`;
  a.click();
  URL.revokeObjectURL(url);
}

/* ═══════════════════════════════════════════════════════════════
   exportCSV(trades) → triggers browser download
═══════════════════════════════════════════════════════════════ */
export function exportCSV(trades) {
  if (!trades || !trades.length) return;
  const headers = [
    'date','time','exitDate','exitTime','pair','dir','entry','sl','tp',
    'actualEntry','actualExit','lotSize','pips','result','rr','actualRR',
    'strat','session','emotion','marketCondition','htfBias','htfTf','entryTf',
    'setupGrade','followedPlan','entryType','exitType','confluences',
    'confidence','discipline','stressLevel','newsPresent','notes','review','riskPct'
  ];
  const escape = v => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return s.includes(',') || s.includes('"') || s.includes('\n')
      ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const rows = [
    headers.join(','),
    ...sortedByDate(trades).map(t => headers.map(h => escape(t[h])).join(','))
  ];
  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = `kfx_trades_${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}
/* ═══════════════════════════════════════════════════════════════
   autoCalc(entry, sl, tp, dir, pair) → { pips, rr }
   Used by ChartPage to compute pips distance and R:R for a
   pending trade before it is logged.
═══════════════════════════════════════════════════════════════ */
export function autoCalc(entry, sl, tp, dir, pair = '') {
  const e = parseFloat(entry);
  const s = parseFloat(sl);
  const t = parseFloat(tp);
  if (!e || !s || !t) return { pips: '', rr: '' };

  // Pip size: JPY pairs and indices use 0.01; everything else 0.0001
  // Gold (XAUUSD) uses 0.1; Silver (XAGUSD) and indices use 1 point
  const p = pair.toUpperCase();
  let pipSize;
  if (p === 'XAUUSD')                                    pipSize = 0.1;
  else if (p === 'XAGUSD')                               pipSize = 0.01;
  else if (/JPY/.test(p))                                pipSize = 0.01;
  else if (/NASDAQ|US30|US500|GER30|UK100|JP225|AUS200|FRA40/.test(p)) pipSize = 1;
  else if (/BTC|ETH|BNB|SOL|XRP/.test(p))               pipSize = 1;
  else                                                   pipSize = 0.0001;

  const isLong   = (dir || '').toUpperCase() === 'BUY' || (dir || '').toUpperCase() === 'LONG';
  const riskPts  = Math.abs(e - s);
  const rewardPts = Math.abs(t - e);
  const pips     = isLong ? (t - e) / pipSize : (e - t) / pipSize;
  const rr       = riskPts > 0 ? (rewardPts / riskPts).toFixed(2) : '';

  return {
    pips:  pips.toFixed(0),
    rr,
  };
}

/* ═══════════════════════════════════════════════════════════════
   calcAmount(entry, sl, tp, dir, lotSize, pair) → number | null
   Returns estimated dollar P&L for the trade, or null if inputs
   are insufficient.
═══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════
   getPipSpecs(pair) → { pipValue, pipSize }
   Single source of truth for per-instrument pip/point sizing.
   Used by calcAmount() (P&L) and the Risk-of-Account calc in
   TradeModal so both agree on the same $ value per pip/point.
═══════════════════════════════════════════════════════════════ */
export function getPipSpecs(pair = '') {
  const p = (pair || '').toUpperCase();

  let pipValue;
  if (p === 'XAUUSD')                                              pipValue = 10;   // $10/pip per lot
  else if (p === 'XAGUSD')                                         pipValue = 50;
  else if (/JPY/.test(p))                                          pipValue = 1000; // ~$9.xx, approx 1000 JPY
  else if (/NASDAQ|US30|US500|GER30|UK100|JP225|AUS200|FRA40/.test(p)) pipValue = 20; // $20/point per 1.0 lot (standard index CFD contract)
  else if (/BTC|ETH|BNB|SOL|XRP/.test(p))                         pipValue = 1;
  else                                                             pipValue = 10;   // $10/pip per standard lot

  let pipSize;
  if (p === 'XAUUSD')                                              pipSize = 0.1;
  else if (p === 'XAGUSD')                                         pipSize = 0.01;
  else if (/JPY/.test(p))                                          pipSize = 0.01;
  else if (/NASDAQ|US30|US500|GER30|UK100|JP225|AUS200|FRA40/.test(p)) pipSize = 1;
  else if (/BTC|ETH|BNB|SOL|XRP/.test(p))                         pipSize = 1;
  else                                                             pipSize = 0.0001;

  return { pipValue, pipSize };
}

export function calcAmount(entry, sl, tp, dir, lotSize, pair = '') {
  const e    = parseFloat(entry);
  const t    = parseFloat(tp);
  const lot  = parseFloat(lotSize);
  if (!e || !t || !lot) return null;

  const isLong = (dir || '').toUpperCase() === 'BUY' || (dir || '').toUpperCase() === 'LONG';
  const { pipValue, pipSize } = getPipSpecs(pair);

  const priceDiff = isLong ? (t - e) : (e - t);
  const pips      = priceDiff / pipSize;
  const amount    = r2(pips * pipValue * lot);
  return amount;
}

/* ═══════════════════════════════════════════════════════════════
   calcRiskAmount(entry, sl, lotSize, pair) → number | null
   Dollar value at risk (always positive), using the same
   pip/point specs as calcAmount so Risk % and Estimated P&L
   stay consistent for every instrument.
═══════════════════════════════════════════════════════════════ */
export function calcRiskAmount(entry, sl, lotSize, pair = '') {
  const e   = parseFloat(entry);
  const sl_ = parseFloat(sl);
  const lot = parseFloat(lotSize);
  if (!e || !sl_ || !lot) return null;

  const { pipValue, pipSize } = getPipSpecs(pair);
  const pips = Math.abs(e - sl_) / pipSize;
  return r2(pips * pipValue * lot);
}

/* ═══════════════════════════════════════════════════════════════
   autoDetectSession(time) → session string | ''
   Maps a local HH:MM time string to a trading session name.
   Sessions are based on EST kill zones (same as ChartPage):
     Asian      20:00–00:00 EST  →  UTC 01:00–05:00
     London     02:00–05:00 EST  →  UTC 07:00–10:00
     New York   07:00–12:00 EST  →  UTC 12:00–17:00
   Note: we compare against UTC hour derived from the browser's
   local time so this works regardless of the user's timezone.
═══════════════════════════════════════════════════════════════ */
export function autoDetectSession(time) {
  if (!time) return '';
  const [hStr, mStr] = time.split(':');
  const h = parseInt(hStr, 10);
  const m = parseInt(mStr || '0', 10);
  if (isNaN(h)) return '';

  // Build a Date for today at the given local HH:MM, then read UTC hour
  const now = new Date();
  const d   = new Date(now.getFullYear(), now.getMonth(), now.getDate(), h, m);
  const utcH = d.getUTCHours();
  const utcM = d.getUTCMinutes();
  const mins = utcH * 60 + utcM;

  // UTC windows (inclusive start, exclusive end)
  // Asian:    01:00–05:00 UTC  (EST 20:00–00:00)
  // London:   07:00–10:00 UTC  (EST 02:00–05:00)
  // NY Am:    12:00–17:00 UTC  (EST 07:00–12:00)
  // London-NY overlap: 12:00–14:00 UTC
  if (mins >= 1*60  && mins < 5*60)  return 'Asian';
  if (mins >= 7*60  && mins < 10*60) return 'London';
  if (mins >= 12*60 && mins < 14*60) return 'London-NY';
  if (mins >= 14*60 && mins < 17*60) return 'New York';
  return '';
}

/* ═══════════════════════════════════════════════════════════════
   calcDuration(date, entryTime, exitTime, exitDate?) -> string | ''
   Returns a human-readable duration: "45m", "2h 15m", "3d 2h 15m".
   date:      'YYYY-MM-DD'  -- entry date
   entryTime: 'HH:MM'
   exitTime:  'HH:MM'
   exitDate:  'YYYY-MM-DD'  -- optional explicit exit date.
              When provided, used directly (supports same-day,
              overnight, and multi-day swing trades).
              When omitted, falls back to the overnight heuristic:
              if exit timestamp < entry, assume +1 day.
═══════════════════════════════════════════════════════════════ */
export function calcDuration(date, entryTime, exitTime, exitDate) {
  if (!date || !entryTime || !exitTime) return '';
  try {
    const base = new Date(`${date}T${entryTime}`);
    let   exit = exitDate
      ? new Date(`${exitDate}T${exitTime}`)
      : new Date(`${date}T${exitTime}`);
    if (isNaN(base.getTime()) || isNaN(exit.getTime())) return '';
    // Overnight heuristic -- only when no explicit exitDate was given
    if (!exitDate && exit < base) exit = new Date(exit.getTime() + 86400000);
    const diffMs = exit - base;
    const totalM = Math.round(diffMs / 60000);
    if (totalM <= 0) return '';
    const days  = Math.floor(totalM / (60 * 24));
    const hours = Math.floor((totalM % (60 * 24)) / 60);
    const mins  = totalM % 60;
    if (days > 0) {
      if (hours === 0 && mins === 0) return `${days}d`;
      if (mins  === 0) return `${days}d ${hours}h`;
      if (hours === 0) return `${days}d ${mins}m`;
      return `${days}d ${hours}h ${mins}m`;
    }
    if (hours === 0) return `${mins}m`;
    if (mins  === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  } catch {
    return '';
  }
}

/* ═══════════════════════════════════════════════════════════════
   localStorage persistence helpers
   loadTrades()           → Trade[]
   saveTrades(trades)     → void
   loadNextId()           → number
   saveNextId(id)         → void
═══════════════════════════════════════════════════════════════ */
const LS_TRADES  = 'kfx_trades';
const LS_NEXT_ID = 'kfx_next_id';

export function loadTrades() {
  try {
    const raw = localStorage.getItem(LS_TRADES);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

export function saveTrades(trades) {
  try {
    localStorage.setItem(LS_TRADES, JSON.stringify(trades));
  } catch (e) {
    console.error('saveTrades failed:', e);
  }
}

export function loadNextId() {
  try {
    const raw = localStorage.getItem(LS_NEXT_ID);
    const n   = parseInt(raw, 10);
    return isFinite(n) && n > 0 ? n : 1;
  } catch {
    return 1;
  }
}

export function saveNextId(id) {
  try {
    localStorage.setItem(LS_NEXT_ID, String(id));
  } catch (e) {
    console.error('saveNextId failed:', e);
  }
}